import { useRouter } from '@sls-react/routing';
import displayPrice from '@sls-react/utils/displayPrice';
import { useQuery } from 'react-query';
import { fetchExchangeRate } from '@sls-react/api/sellerAccount';
import { useCallback } from 'react';
import localeToCurrency from '@sls-react/utils/localeToCurrency';
import { days1inMS } from '../constants/times';

interface UsePriceOptions {
  dataSourceCurrency?: string;
  decimals?: boolean;
}

export default function usePrice(
  options: UsePriceOptions = { dataSourceCurrency: 'USD' },
) {
  const { locale } = useRouter();
  const currency = localeToCurrency(locale);

  const { data: exchangeRate } = useQuery(
    ['exchange-rate', currency],
    () => fetchExchangeRate(currency),
    {
      staleTime: days1inMS,
      enabled: currency !== options.dataSourceCurrency,
    },
  );

  return useCallback(
    (price?: number) => {
      let finalPrice = price;
      let priceChangedByExchangeRate = false;
      const useExchangeRate = currency !== options.dataSourceCurrency;

      if (useExchangeRate && price != null && exchangeRate?.rate) {
        finalPrice = +price * exchangeRate.rate;
        priceChangedByExchangeRate = true;
      }

      return {
        currency: priceChangedByExchangeRate
          ? currency
          : options.dataSourceCurrency,
        formattedPrice: displayPrice(finalPrice, options.decimals),
        price: finalPrice,
        nonUSDCurrency: currency !== 'USD' ? currency : false,
      };
    },
    [
      currency,
      exchangeRate?.rate,
      options.dataSourceCurrency,
      options.decimals,
    ],
  );
}
